import React, { useState, useCallback, useMemo } from "react"
import { useNavigate } from "react-router-dom"
import { useTypedSelector } from "../app/store"
import HeaderSection from "../components/header-section"
import Sidebar from "../features/sidebar/components/sidebar"
import {
  useCreateTeamSimulationMutation,
  useSendTeamInvitationsMutation,
  useGetUsersByOrganizationQuery,
} from "../app/services/meseeknaApiV2"

// Hardcoded Coastarea scenario
// Note: scenarioId references the tests.id column - Coastarea is test ID 102
const COASTAREA_SCENARIO_ID = 102
const COASTAREA_SCENARIO_NAME = "Coastarea Crisis Simulation"

type ParticipantEmail = {
  id: string
  email: string
  name?: string
  isOrgUser?: boolean
  userId?: number
}

type OrgUser = {
  id: number
  firstName: string
  lastName: string
  email: string
}

type Step = 1 | 2 | 3

const TeamSimulationSetup: React.FC = () => {
  const navigate = useNavigate()
  const organization = useTypedSelector((state) => state.authSlice.organization)
  const user = useTypedSelector((state) => state.authSlice.user)
  const organizationId = user?.organizationId || 0

  // Step navigation state
  const [currentStep, setCurrentStep] = useState<Step>(1)

  // Step 1: Participant selection
  const [selectedParticipants, setSelectedParticipants] = useState<ParticipantEmail[]>([])
  const [manualEmail, setManualEmail] = useState("")
  const [userSearchQuery, setUserSearchQuery] = useState("")

  // Step 2: Schedule
  const [scheduledDate, setScheduledDate] = useState("")
  const [scheduledTime, setScheduledTime] = useState("")
  const [timezone, setTimezone] = useState(() => {
    return Intl.DateTimeFormat().resolvedOptions().timeZone
  })

  // Common US timezones for the dropdown
  const timezones = [
    { value: "America/New_York", label: "Eastern Time (ET)" },
    { value: "America/Chicago", label: "Central Time (CT)" },
    { value: "America/Denver", label: "Mountain Time (MT)" },
    { value: "America/Los_Angeles", label: "Pacific Time (PT)" },
    { value: "America/Anchorage", label: "Alaska Time (AKT)" },
    { value: "Pacific/Honolulu", label: "Hawaii Time (HT)" },
    { value: "Europe/London", label: "London (GMT/BST)" },
    { value: "Europe/Paris", label: "Central European (CET)" },
    { value: "Asia/Tokyo", label: "Japan (JST)" },
    { value: "Australia/Sydney", label: "Sydney (AEST)" },
  ]

  // Form state
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})

  // API mutations
  const [createTeamSimulation] = useCreateTeamSimulationMutation()
  const [sendTeamInvitations] = useSendTeamInvitationsMutation()

  // Fetch organization users
  const { data: orgUsersData, isLoading: isLoadingUsers } = useGetUsersByOrganizationQuery(
    organizationId,
    { skip: !organizationId }
  )

  // Filter org users based on search query and exclude already selected
  const filteredOrgUsers = useMemo(() => {
    const users = (orgUsersData?.data || []) as OrgUser[]
    const selectedEmails = new Set(selectedParticipants.map(p => p.email.toLowerCase()))

    return users
      .filter(u => !selectedEmails.has(u.email.toLowerCase()))
      .filter(u => {
        if (!userSearchQuery) return true
        const query = userSearchQuery.toLowerCase()
        return (
          u.firstName?.toLowerCase().includes(query) ||
          u.lastName?.toLowerCase().includes(query) ||
          u.email.toLowerCase().includes(query)
        )
      })
  }, [orgUsersData, selectedParticipants, userSearchQuery])

  // Email validation
  const isValidEmail = (email: string) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
    return emailRegex.test(email.trim())
  }

  // Add org user to selection
  const addOrgUser = useCallback((user: OrgUser) => {
    setSelectedParticipants(prev => [
      ...prev,
      {
        id: `org-${user.id}`,
        email: user.email,
        name: `${user.firstName} ${user.lastName}`,
        isOrgUser: true,
        userId: user.id
      }
    ])
    setUserSearchQuery("")
  }, [])

  // Add manual email
  const addManualEmail = useCallback(() => {
    const email = manualEmail.trim()
    if (!email) return

    if (!isValidEmail(email)) {
      setErrors(prev => ({ ...prev, manualEmail: "Please enter a valid email address" }))
      return
    }

    // Check for duplicates
    if (selectedParticipants.some(p => p.email.toLowerCase() === email.toLowerCase())) {
      setErrors(prev => ({ ...prev, manualEmail: "This email is already added" }))
      return
    }

    setSelectedParticipants(prev => [
      ...prev,
      {
        id: `manual-${Date.now()}`,
        email,
        isOrgUser: false
      }
    ])
    setManualEmail("")
    setErrors(prev => {
      const newErrors = { ...prev }
      delete newErrors.manualEmail
      return newErrors
    })
  }, [manualEmail, selectedParticipants])

  // Remove participant
  const removeParticipant = useCallback((id: string) => {
    setSelectedParticipants(prev => prev.filter(p => p.id !== id))
  }, [])

  // Auto-generate simulation name from date/time
  const generateSimulationName = useCallback(() => {
    if (!scheduledDate || !scheduledTime) return "Team Simulation"
    const date = new Date(`${scheduledDate}T${scheduledTime}`)
    const formatted = date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    })
    return `Team Sim - ${formatted}`
  }, [scheduledDate, scheduledTime])

  // Validate current step
  const validateStep = useCallback((step: Step): boolean => {
    const newErrors: Record<string, string> = {}

    if (step === 1) {
      if (selectedParticipants.length < 2) {
        newErrors.participants = "Please add at least 2 participants for a team simulation"
      }
    }

    if (step === 2) {
      if (!scheduledDate) {
        newErrors.scheduledDate = "Please select a date"
      }
      if (!scheduledTime) {
        newErrors.scheduledTime = "Please select a time"
      }
      if (scheduledDate && scheduledTime) {
        const scheduledDateTime = new Date(`${scheduledDate}T${scheduledTime}`)
        if (scheduledDateTime <= new Date()) {
          newErrors.scheduledDate = "Scheduled time must be in the future"
        }
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }, [selectedParticipants, scheduledDate, scheduledTime])

  // Navigate to next step
  const goToNextStep = useCallback(() => {
    if (!validateStep(currentStep)) return
    if (currentStep < 3) {
      setCurrentStep((currentStep + 1) as Step)
    }
  }, [currentStep, validateStep])

  // Navigate to previous step
  const goToPreviousStep = useCallback(() => {
    if (currentStep > 1) {
      setCurrentStep((currentStep - 1) as Step)
    }
  }, [currentStep])

  // Handle form submission
  const handleSubmit = useCallback(async () => {
    if (!validateStep(3)) return

    setIsSubmitting(true)
    setErrors({})

    try {
      const validEmails = selectedParticipants.map(p => p.email.trim())
      const scheduledStartTime = `${scheduledDate}T${scheduledTime}:00`
      const simulationName = generateSimulationName()

      const teamSimulationData = {
        scenarioId: COASTAREA_SCENARIO_ID,
        name: simulationName,
        participantEmails: validEmails,
        organizationId: parseInt(organization?.id || "0"),
        createdBy: user?.id,
        scheduledStartTime,
        timezone
      }

      // Step 1: Create the team simulation
      const result = await createTeamSimulation(teamSimulationData).unwrap()

      console.log("Team simulation created:", result)

      // Step 2: Send invitation emails with calendar invites
      try {
        const adminName = `${user?.firstName || ""} ${user?.lastName || ""}`.trim() || "Administrator"
        const emailData = {
          teamSimulationId: result.teamSimulationId,
          simulationName,
          scenarioName: COASTAREA_SCENARIO_NAME,
          adminName,
          participantEmails: validEmails,
          organizationId: parseInt(organization?.id || "0"),
          scheduledStartTime,
          timezone
        }

        const emailResult = await sendTeamInvitations(emailData).unwrap()

        console.log("Invitation emails sent:", emailResult)

        // Success - navigate to team simulations list
        console.log(`Team simulation "${simulationName}" created successfully! ${emailResult.emailsSent || validEmails.length} invitations sent.`)
        navigate("/admin/team-simulations")

      } catch (emailError: unknown) {
        // Simulation created but emails failed - still navigate away
        console.warn("Team simulation created but email sending failed:", emailError)
        navigate("/admin/team-simulations")
      }

    } catch (error: unknown) {
      console.error("Failed to create team simulation:", error)
      const errorMsg = (error as { data?: { message?: string }; message?: string })?.data?.message || (error as { message?: string })?.message || "Failed to create team simulation"
      setErrors({
        submit: errorMsg
      })
    } finally {
      setIsSubmitting(false)
    }
  }, [validateStep, selectedParticipants, scheduledDate, scheduledTime, timezone, organization, user, createTeamSimulation, sendTeamInvitations, generateSimulationName, navigate])

  // Step indicator component
  const stepLabels = ["Invite Team", "Schedule", "Confirm"]

  const StepIndicator = () => (
    <div className="flex flex-col items-center mb-8">
      {/* Circles and connecting lines */}
      <div className="flex items-center">
        {[1, 2, 3].map((step) => (
          <React.Fragment key={step}>
            <div
              className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold flex-shrink-0 ${
                step === currentStep
                  ? "bg-blue-600 text-white"
                  : step < currentStep
                  ? "bg-green-500 text-white"
                  : "bg-gray-200 text-gray-600"
              }`}
            >
              {step < currentStep ? (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                step
              )}
            </div>
            {step < 3 && (
              <div className={`w-24 h-1 ${
                step < currentStep ? "bg-green-500" : "bg-gray-200"
              }`} />
            )}
          </React.Fragment>
        ))}
      </div>
      {/* Labels row */}
      <div className="flex items-start mt-2">
        {[1, 2, 3].map((step) => (
          <React.Fragment key={step}>
            <span className={`w-10 text-center text-sm font-medium flex-shrink-0 ${
              step === currentStep ? "text-blue-600" : "text-gray-500"
            }`}>
              {stepLabels[step - 1]}
            </span>
            {step < 3 && <div className="w-24" />}
          </React.Fragment>
        ))}
      </div>
    </div>
  )

  // Step 1: Invite Team
  const renderStep1 = () => (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <h2 className="text-xl font-semibold text-gray-900 mb-2">Invite Team Members</h2>
      <p className="text-sm text-gray-600 mb-6">
        Select team members from your organization or add external participants by email.
      </p>

      {errors.participants && (
        <div className="bg-red-50 border border-red-200 rounded-md p-3 mb-4">
          <p className="text-red-600 text-sm">{errors.participants}</p>
        </div>
      )}

      {/* Selected participants */}
      {selectedParticipants.length > 0 && (
        <div className="mb-6">
          <h3 className="text-sm font-medium text-gray-700 mb-3">
            Selected Participants ({selectedParticipants.length})
          </h3>
          <div className="flex flex-wrap gap-2">
            {selectedParticipants.map((participant) => (
              <div
                key={participant.id}
                className="flex items-center bg-blue-50 border border-blue-200 rounded-full px-3 py-1"
              >
                <span className="text-sm text-blue-800">
                  {participant.name || participant.email}
                </span>
                <button
                  type="button"
                  onClick={() => removeParticipant(participant.id)}
                  className="ml-2 text-blue-600 hover:text-blue-800"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Organization users */}
      <div className="mb-6">
        <h3 className="text-sm font-medium text-gray-700 mb-3">Organization Members</h3>
        <input
          type="text"
          value={userSearchQuery}
          onChange={(e) => setUserSearchQuery(e.target.value)}
          placeholder="Search by name or email..."
          className="w-full px-3 py-2 border border-gray-300 rounded-md mb-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
        />

        {isLoadingUsers ? (
          <div className="flex items-center justify-center py-4">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
            <span className="ml-2 text-gray-600">Loading users...</span>
          </div>
        ) : filteredOrgUsers.length === 0 ? (
          <p className="text-gray-500 text-sm py-4">
            {userSearchQuery ? "No matching users found" : "No available users"}
          </p>
        ) : (
          <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-md">
            {filteredOrgUsers.map((orgUser) => (
              <div
                key={orgUser.id}
                onClick={() => addOrgUser(orgUser)}
                className="flex items-center justify-between p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
              >
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    {orgUser.firstName} {orgUser.lastName}
                  </p>
                  <p className="text-xs text-gray-500">{orgUser.email}</p>
                </div>
                <button
                  type="button"
                  className="text-blue-600 hover:text-blue-800"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Manual email entry */}
      <div>
        <h3 className="text-sm font-medium text-gray-700 mb-3">Add External Participant</h3>
        <div className="flex gap-2">
          <div className="flex-1">
            <input
              type="email"
              value={manualEmail}
              onChange={(e) => {
                setManualEmail(e.target.value)
                if (errors.manualEmail) {
                  setErrors(prev => {
                    const newErrors = { ...prev }
                    delete newErrors.manualEmail
                    return newErrors
                  })
                }
              }}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  addManualEmail()
                }
              }}
              placeholder="Enter email address"
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.manualEmail ? "border-red-500" : "border-gray-300"
              }`}
            />
            {errors.manualEmail && (
              <p className="text-red-600 text-sm mt-1">{errors.manualEmail}</p>
            )}
          </div>
          <button
            type="button"
            onClick={addManualEmail}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            Add
          </button>
        </div>
      </div>
    </div>
  )

  // Step 2: Schedule
  const renderStep2 = () => (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <h2 className="text-xl font-semibold text-gray-900 mb-2">Schedule the Simulation</h2>
      <p className="text-sm text-gray-600 mb-6">
        Everyone joins together at the scheduled time. Calendar invites are sent automatically.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div>
          <label htmlFor="scheduledDate" className="block text-sm font-medium text-gray-700 mb-2">
            Date *
          </label>
          <input
            type="date"
            id="scheduledDate"
            value={scheduledDate}
            onChange={(e) => setScheduledDate(e.target.value)}
            min={new Date().toLocaleDateString('en-CA')}
            className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errors.scheduledDate ? "border-red-500" : "border-gray-300"
            }`}
          />
          {errors.scheduledDate && (
            <p className="text-red-600 text-sm mt-1">{errors.scheduledDate}</p>
          )}
        </div>

        <div>
          <label htmlFor="scheduledTime" className="block text-sm font-medium text-gray-700 mb-2">
            Time *
          </label>
          <input
            type="time"
            id="scheduledTime"
            value={scheduledTime}
            onChange={(e) => setScheduledTime(e.target.value)}
            className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errors.scheduledTime ? "border-red-500" : "border-gray-300"
            }`}
          />
          {errors.scheduledTime && (
            <p className="text-red-600 text-sm mt-1">{errors.scheduledTime}</p>
          )}
        </div>

        <div>
          <label htmlFor="timezone" className="block text-sm font-medium text-gray-700 mb-2">
            Timezone
          </label>
          <select
            id="timezone"
            value={timezone}
            onChange={(e) => setTimezone(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {timezones.map((tz) => (
              <option key={tz.value} value={tz.value}>
                {tz.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Schedule summary */}
      {scheduledDate && scheduledTime && (
        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-md">
          <h3 className="text-sm font-medium text-blue-900 mb-2">Scheduled For</h3>
          <p className="text-blue-800">
            {new Date(`${scheduledDate}T${scheduledTime}`).toLocaleDateString('en-US', {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })} at {new Date(`${scheduledDate}T${scheduledTime}`).toLocaleTimeString('en-US', {
              hour: 'numeric',
              minute: '2-digit'
            })} ({timezones.find(tz => tz.value === timezone)?.label || timezone})
          </p>
        </div>
      )}
    </div>
  )

  // Step 3: Confirmation
  const renderStep3 = () => (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
      <div className="mb-6">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h2 className="text-2xl font-semibold text-gray-900 mb-2">Review &amp; Create Your Simulation</h2>
        <p className="text-gray-600">
          Confirm the details below, then click &quot;Create Team Simulation&quot; to send invitations.
        </p>
      </div>

      {/* Summary card */}
      <div className="bg-gray-50 rounded-lg p-6 text-left mb-6">
        <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-4">Simulation Details</h3>

        <div className="space-y-4">
          <div className="flex items-start">
            <div className="flex-shrink-0 w-8">
              <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900">{selectedParticipants.length} Participants</p>
              <p className="text-sm text-gray-500">
                {selectedParticipants.slice(0, 3).map(p => p.name || p.email).join(", ")}
                {selectedParticipants.length > 3 && ` +${selectedParticipants.length - 3} more`}
              </p>
            </div>
          </div>

          <div className="flex items-start">
            <div className="flex-shrink-0 w-8">
              <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900">
                {scheduledDate && scheduledTime ? new Date(`${scheduledDate}T${scheduledTime}`).toLocaleDateString('en-US', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                }) : "Not scheduled"}
              </p>
              <p className="text-sm text-gray-500">
                {scheduledTime && new Date(`${scheduledDate}T${scheduledTime}`).toLocaleTimeString('en-US', {
                  hour: 'numeric',
                  minute: '2-digit'
                })} ({timezones.find(tz => tz.value === timezone)?.label || timezone})
              </p>
            </div>
          </div>

          <div className="flex items-start">
            <div className="flex-shrink-0 w-8">
              <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900">{COASTAREA_SCENARIO_NAME}</p>
              <p className="text-sm text-gray-500">45-60 minute collaborative experience</p>
            </div>
          </div>
        </div>
      </div>

      {/* What happens next */}
      <div className="bg-blue-50 rounded-lg p-4 text-left">
        <h4 className="text-sm font-medium text-blue-900 mb-2">What happens next?</h4>
        <ul className="text-sm text-blue-800 space-y-1">
          <li className="flex items-center">
            <svg className="w-4 h-4 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            Each participant receives an email with joining instructions
          </li>
          <li className="flex items-center">
            <svg className="w-4 h-4 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            Calendar invites are sent automatically
          </li>
          <li className="flex items-center">
            <svg className="w-4 h-4 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            Everyone joins together at the scheduled time
          </li>
        </ul>
      </div>
    </div>
  )

  return (
    <div className="relative bg-base-white w-full h-[100dvh] flex flex-row items-start justify-start font-text-md-regular">
      <Sidebar />
      <div className="w-full h-[100dvh] flex-1 flex flex-col">
        <HeaderSection />

        <div className="flex-1 overflow-y-auto p-8">
          <div className="max-w-3xl mx-auto">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Create Team Simulation</h1>
            <p className="text-gray-600 mb-8">See how your team thinks and collaborates—in real time.</p>

            <StepIndicator />

            {/* Step content */}
            {currentStep === 1 && renderStep1()}
            {currentStep === 2 && renderStep2()}
            {currentStep === 3 && renderStep3()}

            {/* Submit Errors */}
            {errors.submit && (
              <div className="mt-6 bg-red-50 border border-red-200 rounded-md p-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-red-600 text-sm">{errors.submit}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Navigation buttons */}
            <div className="mt-8 flex justify-between">
              <div>
                {currentStep > 1 && (
                  <button
                    type="button"
                    onClick={goToPreviousStep}
                    className="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    Back
                  </button>
                )}
              </div>

              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={() => navigate("/admin/team-simulations")}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  Cancel
                </button>

                {currentStep < 3 ? (
                  <button
                    type="button"
                    onClick={goToNextStep}
                    className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    Continue
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                  >
                    {isSubmitting && (
                      <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    )}
                    {isSubmitting ? "Creating..." : "Create Team Simulation"}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default TeamSimulationSetup
